import { Link } from "react-router-dom";
import { Phone, MessageCircle, MapPin, Instagram } from "lucide-react";
import { HOTEL } from "@/data/hotel";
import { trackEvent } from "@/lib/analytics";

const trackContact = (channel, placement) => () => trackEvent("contact_click", { channel, placement });

const EXPLORE_LINKS = [
  { to: "/properties", label: "Our Hotels", testId: "footer-link-properties" },
  { to: "/rooms", label: "Rooms", testId: "footer-link-rooms" },
  { to: "/gallery", label: "Gallery", testId: "footer-link-gallery" },
  { to: "/careers", label: "Careers", testId: "footer-link-careers" },
  { to: "/partner", label: "Partner With Us", testId: "footer-link-partner" },
  { to: "/contact", label: "Contact", testId: "footer-link-contact" },
  { to: "/book", label: "Book Now", testId: "footer-link-book" },
];

const DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=Hotel+Azulo+Inn,+B1/16,+Safdarjung+Enclave,+New+Delhi,+Delhi+110029";

const pillBtn =
  "inline-flex items-center gap-1.5 h-9 px-4 border border-ink-900/25 text-ink-900 text-[10px] font-semibold tracking-[0.18em] uppercase hover:bg-ink-900 hover:text-paper-50 transition-colors";

const FooterBrand = () => (
  <div className="md:col-span-2">
    <img src={HOTEL.logo} alt={HOTEL.name} className="h-[115px] w-auto" data-testid="footer-logo" />
    <p className="mt-6 max-w-sm text-ink-600 leading-relaxed">
      {HOTEL.tagline} A well-kept boutique hotel at B1/16 Safdarjung Enclave — easy walking
      distance to Bhikaji Cama Place.
    </p>
    <div className="mt-8 overline">14 rooms · Safdarjung Enclave · New Delhi</div>
    <a
      href="https://www.instagram.com/azulohotels"
      target="_blank"
      rel="noreferrer"
      data-testid="footer-btn-instagram"
      className="mt-6 inline-flex items-center gap-2 h-10 px-5 border border-ink-900/25 text-ink-900 text-[10px] font-semibold tracking-[0.18em] uppercase hover:bg-ink-900 hover:text-paper-50 transition-colors"
    >
      <Instagram size={14} /> Follow us on Instagram
    </a>
  </div>
);

const FooterVisit = () => (
  <div>
    <div className="overline">Visit</div>
    <p className="mt-4 text-ink-800 leading-relaxed text-sm">{HOTEL.address}</p>
    <a
      href={`tel:${HOTEL.phone.replace(/\s|-/g, "")}`}
      data-testid="footer-phone"
      onClick={trackContact("phone", "footer_text")}
      className="mt-4 block text-ink-800 hover:text-sage-dark text-sm"
    >
      {HOTEL.phone}
    </a>
    <a
      href={`tel:${HOTEL.mobile.replace(/\s|-|\+/g, "")}`}
      data-testid="footer-mobile"
      onClick={trackContact("phone", "footer_text")}
      className="mt-1 block text-ink-800 hover:text-sage-dark text-sm"
    >
      {HOTEL.mobile}
    </a>
    <a
      href={`mailto:${HOTEL.email}`}
      data-testid="footer-email"
      className="mt-1 block text-ink-800 hover:text-sage-dark text-sm"
    >
      {HOTEL.email}
    </a>
    <div className="mt-6 flex flex-wrap gap-2">
      <a
        href={`tel:${HOTEL.phone.replace(/\s|-/g, "")}`}
        data-testid="footer-btn-phone"
        onClick={trackContact("phone", "footer_button")}
        className="inline-flex items-center gap-1.5 h-9 px-4 bg-sage text-paper-50 text-[10px] font-semibold tracking-[0.18em] uppercase hover:bg-sage-dark transition-colors"
      >
        <Phone size={12} /> Front Desk
      </a>
      <a
        href={`https://wa.me/${HOTEL.whatsapp}`}
        target="_blank"
        rel="noreferrer"
        data-testid="footer-btn-whatsapp"
        onClick={trackContact("whatsapp", "footer_button")}
        className={pillBtn}
      >
        <MessageCircle size={12} /> WhatsApp
      </a>
      <a
        href={DIRECTIONS_URL}
        target="_blank"
        rel="noreferrer"
        data-testid="footer-btn-directions"
        className={pillBtn}
      >
        <MapPin size={12} /> Directions
      </a>
    </div>
  </div>
);

const FooterExplore = () => (
  <div>
    <div className="overline">Explore</div>
    <ul className="mt-4 space-y-3 text-sm">
      {EXPLORE_LINKS.map((l) => (
        <li key={l.to}>
          <Link data-testid={l.testId} className="text-ink-800 hover:text-sage-dark" to={l.to}>
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export const Footer = () => (
  <footer data-testid="site-footer" className="relative mt-32 border-t hairline bg-paper-100">
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 grid grid-cols-1 md:grid-cols-4 gap-12">
      <FooterBrand />
      <FooterVisit />
      <FooterExplore />
    </div>
    <div className="border-t hairline">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between text-xs text-ink-500 gap-3">
        <div>© {new Date().getFullYear()} Marwon Group · Since 1980. All rights reserved.</div>
        <div className="tracking-[0.22em] uppercase">Safdarjung Enclave · New Delhi</div>
      </div>
    </div>
  </footer>
);

export default Footer;
